
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Faq } from "@/types/faq-types";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface FaqsTableProps {
  faqs: Faq[];
  onEdit: (faq: Faq) => void;
  onDelete: (id: string) => Promise<boolean>;
}

const FaqsTable = ({ faqs, onEdit, onDelete }: FaqsTableProps) => {
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  const handleConfirmDelete = (id: string) => {
    setConfirmDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    
    setIsDeleting(true);
    try {
      await onDelete(confirmDeleteId);
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setConfirmDeleteId(null);
    }
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[250px]">Question</TableHead>
            <TableHead className="w-[300px]">Answer</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Order</TableHead>
            <TableHead>Has Image</TableHead>
            <TableHead>Has Video</TableHead>
            <TableHead>Last Updated</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {faqs.map((faq) => (
            <TableRow key={faq.id}>
              <TableCell className="font-medium">{truncateText(faq.question)}</TableCell>
              <TableCell>{truncateText(faq.answer)}</TableCell>
              <TableCell>
                {faq.status === "published" ? (
                  <Badge className="bg-green-500">Published</Badge>
                ) : (
                  <Badge className="bg-yellow-500">Draft</Badge>
                )}
              </TableCell>
              <TableCell>{faq.display_order}</TableCell>
              <TableCell>{faq.image_url ? "Yes" : "No"}</TableCell>
              <TableCell>{faq.video_url ? "Yes" : "No"}</TableCell>
              <TableCell>{format(new Date(faq.updated_at), "dd MMM yyyy")}</TableCell>
              <TableCell className="text-right space-x-2">
                <Button variant="outline" size="sm" onClick={() => onEdit(faq)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => handleConfirmDelete(faq.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete FAQ</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this FAQ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmDeleteId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FaqsTable;
